import { useWatch } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import type { MediaSetting } from "@circle-react/types/MediaSetting";
import { Form } from "@circle-react-uikit/Form";
import { Typography } from "@circle-react-uikit/Typography";
import { i18nRoot } from "../constants";
import { CustomThumbnailInput } from "./CustomThumbnailInput";
import { TranscriptFileTypeSelect } from "./TranscriptFileTypeSelect";

interface MediaSettingsProps {
  mediaSettings: MediaSetting;
  onCustomThumbnailUrlChange: (url: string | null) => void;
}

export const MediaSettings = ({
  mediaSettings,
  onCustomThumbnailUrlChange,
}: MediaSettingsProps) => {
  const isTranscriptEnabled = useWatch<MediaSetting, "transcript.enabled">({
    name: "transcript.enabled",
  });
  const isTranscriptAiSupported =
    mediaSettings.transcript?.supports_ai ?? false;
  const transcriptAiFileDownloadUrl =
    mediaSettings.transcript?.webvtt_file_url ?? undefined;

  const validateSelectedFile = (selectedFile: string) => {
    if (selectedFile === "ai" && !isTranscriptAiSupported) {
      return t(`${i18nRoot}.transcript.file_type.ai_unsupported_error`);
    }
    return true;
  };

  return (
    <>
      <div className="px-6 py-5">
        <Form.Item
          name="custom_thumbnail"
          translationRoot={i18nRoot}
          hideBorder
          hideDescription
          fullWidth
          className="!p-0"
          labelClassName="!mb-3 !text-base"
        >
          <CustomThumbnailInput
            name="custom_thumbnail"
            title={t(`${i18nRoot}.thumbnail`)}
            description={t(`${i18nRoot}.thumbnail_description`)}
            onImageAttachSuccess={onCustomThumbnailUrlChange}
            onDelete={() => onCustomThumbnailUrlChange(null)}
          />
        </Form.Item>
      </div>
      <div className="border-primary border-t" />
      <div className="flex flex-col gap-4 px-6 py-5">
        <Typography.LabelMd weight="semibold">
          {t(`${i18nRoot}.settings_section`)}
        </Typography.LabelMd>
        <Form.Item
          name="make_downloadable"
          translationRoot={i18nRoot}
          hideBorder
          fullWidth
          inline
          className="!p-0"
        >
          <Form.ToggleSwitch variant="small" name="make_downloadable" />
        </Form.Item>
        <Form.Item
          name="transcript.enabled"
          translationRoot={i18nRoot}
          hideBorder
          fullWidth
          inline
          className="!p-0"
          label={t(`${i18nRoot}.enable_transcription`)}
          description={t(`${i18nRoot}.enable_transcription_description`)}
        >
          <Form.ToggleSwitch variant="small" name="transcript.enabled" />
        </Form.Item>
        {isTranscriptEnabled && (
          <Form.Item
            name="transcript.selected_file"
            hideBorder
            fullWidth
            inline
            hideLabel
            hideDescription
            rules={{ validate: validateSelectedFile }}
            className="flex flex-col !p-0"
          >
            <TranscriptFileTypeSelect
              name="transcript.selected_file"
              rules={{ validate: validateSelectedFile }}
              supportsAi={isTranscriptAiSupported}
              aiFileDownloadUrl={transcriptAiFileDownloadUrl}
            />
          </Form.Item>
        )}
      </div>
    </>
  );
};
